import { hasSscc } from 'support/helpers/packingList/generateSscc';
import { pickPackableQuantity, readQuantity, toFiniteQuantity } from 'support/helpers/packingList/packableQuantity';
import { refIndex } from 'support/helpers/packingList/packingListRefs';
import { WebEDIRowData } from 'support/types/webediTable';

import { groupTopLevelLineItems, LineItemGroup } from '../helpers/groupLineItems';
import { LineItemId, PackingListTree, TransportUnitNode } from '../types';

export const initializeFromData = (
  lineItems: Array<WebEDIRowData>,
  transportUnits: Array<WebEDIRowData>,
  quantityFieldPath = 'shipped_quantity',
): PackingListTree => {
  const groups = groupTopLevelLineItems(lineItems, quantityFieldPath);
  const { entries, lineItemIdByRowIndex } = buildLineItemEntries(lineItems, groups, quantityFieldPath);
  return {
    lineItems: entries,
    transportUnits:
      transportUnits.length === 0
        ? []
        : buildTransportUnitTree(lineItems, transportUnits, lineItemIdByRowIndex, quantityFieldPath),
  };
};

// Maps every row's array index to its parent line-item's index (null for top-level rows), so a
// child can be traced up to the top-level product it belongs to.
const buildParentIndexByRow = (lineItems: Array<WebEDIRowData>): Map<number, number | null> => {
  const parentByRow = new Map<number, number | null>();
  lineItems.forEach((row, index) => parentByRow.set(index, refIndex(row._internal?.parent_line_item)));
  return parentByRow;
};

// Walks parent_line_item refs up to the top-level ancestor's index. The `seen` guard keeps a
// malformed self/cyclic ref from looping forever.
const rootTopLevelIndex = (startIndex: number, parentByRow: Map<number, number | null>): number => {
  let current = startIndex;
  const seen = new Set<number>();
  let parent = parentByRow.get(current) ?? null;
  while (parent !== null && !seen.has(parent)) {
    seen.add(current);
    current = parent;
    parent = parentByRow.get(current) ?? null;
  }
  return current;
};

// Builds the logical line-item entries plus the placement lookup used to attach rows to units.
//
// Top-level rows that share a product (see identifiersMatch) collapse into a single entry keyed by
// the group's first original index, carrying the summed quantity so one card represents the product.
// Every top-level row of that product — each a distinct placement copy the serializer emitted for a
// split — resolves through `lineItemIdByRowIndex` to that one canonical id; keying contents by a
// row's own index instead would reference ids grouping already merged away, dropping placements.
//
// Sub line items are kept once per relative path in the group's canonical (surviving) parent copy.
// The serializer proportionally duplicates a split parent's whole subtree per placement, so the
// initializer sums each physical child path back to its logical total before the next flatten.
const buildLineItemEntries = (
  lineItems: Array<WebEDIRowData>,
  groups: Array<LineItemGroup>,
  quantityFieldPath: string,
): { entries: PackingListTree['lineItems']; lineItemIdByRowIndex: Map<number, LineItemId> } => {
  const entries: PackingListTree['lineItems'] = {};
  const lineItemIdByRowIndex = new Map<number, LineItemId>();
  const parentByRow = buildParentIndexByRow(lineItems);
  const childrenByParentIndex = new Map<number, Array<number>>();

  lineItems.forEach((_row, index) => {
    const parentIndex = parentByRow.get(index) ?? null;
    if (parentIndex === null) {
      return;
    }
    const children = childrenByParentIndex.get(parentIndex) ?? [];
    children.push(index);
    childrenByParentIndex.set(parentIndex, children);
  });

  const canonicalIndexByRootIndex = new Map<number, number>();

  const canonicalTopLevelIndices = new Set<number>();
  groups.forEach((group) => {
    const canonicalIndex = group.indices[0];
    const id: LineItemId = `li-${canonicalIndex}`;
    entries[id] = {
      id,
      originalIndex: canonicalIndex,
      // The single seam where the ordered fallback is applied: everything downstream reads the
      // tree, so it sees the resolved total and must not repeat the choice.
      row: { ...group.mergedRow, [quantityFieldPath]: group.packableQuantity },
    };
    canonicalTopLevelIndices.add(canonicalIndex);
    group.indices.forEach((index) => {
      lineItemIdByRowIndex.set(index, id);
      canonicalIndexByRootIndex.set(index, canonicalIndex);
    });
  });

  const relativeChildPath = (startIndex: number): string => {
    const path: Array<number> = [];
    const seen = new Set<number>();
    let current = startIndex;
    let parent = parentByRow.get(current) ?? null;

    while (parent !== null && !seen.has(current)) {
      seen.add(current);
      const siblingIndex = (childrenByParentIndex.get(parent) ?? []).indexOf(current);
      path.unshift(siblingIndex);
      current = parent;
      parent = parentByRow.get(current) ?? null;
    }

    return path.join('.');
  };

  // A sub line item follows the same rule as its parent — the serializer keeps ordered_quantity on
  // the first placement's copies only — so both totals are summed per PATH before choosing.
  const childQuantitiesByPath = new Map<string, { shipped: number; ordered: number | undefined }>();
  lineItems.forEach((row, index) => {
    if (!row._internal?.parent_line_item) {
      return;
    }
    const canonicalRootIndex = canonicalIndexByRootIndex.get(rootTopLevelIndex(index, parentByRow));
    if (canonicalRootIndex === undefined) {
      return;
    }
    const key = `${canonicalRootIndex}:${relativeChildPath(index)}`;
    const totals = childQuantitiesByPath.get(key) ?? { shipped: 0, ordered: undefined };
    const ordered = toFiniteQuantity(row.ordered_quantity);
    childQuantitiesByPath.set(key, {
      shipped: totals.shipped + readQuantity(row[quantityFieldPath]),
      ordered: ordered === undefined ? totals.ordered : (totals.ordered ?? 0) + ordered,
    });
  });

  lineItems.forEach((row, index) => {
    if (!row._internal?.parent_line_item) {
      return;
    }
    if (!canonicalTopLevelIndices.has(rootTopLevelIndex(index, parentByRow))) {
      return;
    }
    const id: LineItemId = `li-${index}`;
    const canonicalRootIndex = canonicalIndexByRootIndex.get(rootTopLevelIndex(index, parentByRow));
    const key = `${canonicalRootIndex}:${relativeChildPath(index)}`;
    const childTotals = childQuantitiesByPath.get(key);
    entries[id] = {
      id,
      originalIndex: index,
      row:
        childTotals === undefined
          ? row
          : {
              ...row,
              [quantityFieldPath]: pickPackableQuantity(childTotals.ordered, childTotals.shipped),
            },
    };
  });

  return { entries, lineItemIdByRowIndex };
};

const buildTransportUnitTree = (
  lineItems: Array<WebEDIRowData>,
  transportUnits: Array<WebEDIRowData>,
  lineItemIdByRowIndex: Map<number, LineItemId>,
  quantityFieldPath: string,
): Array<TransportUnitNode> => {
  // Index-aligned with the flat input: parent_transport_unit refs (`$['transport_units'][N]`)
  // point at a row's array position, which mirrors the serializer's emitted index. Placeholder
  // (SSCC-less) units are left as empty slots — they are treated as "no transport unit yet", so
  // they never become nodes and their items fall through to unplaced below.
  const transportUnitNodes: Array<TransportUnitNode | undefined> = transportUnits.map((row, index) =>
    hasSscc(row.unit_identifier)
      ? {
          id: String(row.unit_identifier ?? '') || `tu-${index}`,
          unitType: String(row.unit_type ?? ''),
          sscc: row.unit_identifier as string | undefined,
          contents: [],
          children: [],
        }
      : undefined,
  );

  const roots: Array<TransportUnitNode> = [];
  transportUnits.forEach((row, index) => {
    const transportUnitNode = transportUnitNodes[index];
    if (!transportUnitNode) {
      return;
    }
    const parentIndex = refIndex(row._internal?.parent_transport_unit);
    const parentTransportUnitNode = parentIndex === null ? undefined : transportUnitNodes[parentIndex];
    if (!parentTransportUnitNode) {
      roots.push(transportUnitNode);
    } else {
      parentTransportUnitNode.children.push(transportUnitNode);
    }
  });

  lineItems.forEach((row, index) => {
    // Sub line items always follow their parent; they never occupy a unit independently, so
    // only top-level rows become removable contents. The serializer re-derives the subtree.
    if (row._internal?.parent_line_item) {
      return;
    }
    const transportUnitIndex = refIndex(row._internal?.parent_transport_unit);
    const transportUnitNode = transportUnitIndex === null ? undefined : transportUnitNodes[transportUnitIndex];
    if (!transportUnitNode) {
      return;
    }
    const lineItemId = lineItemIdByRowIndex.get(index);
    if (!lineItemId) {
      return;
    }
    // Shipped only — the ordered fallback would invent a placement the user never made.
    transportUnitNode.contents.push({ lineItemId, quantity: readQuantity(row[quantityFieldPath]) });
  });

  return roots;
};
